import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import { connectAdminSocket } from '../socket';

// ============================================================================
// ReceiptPage — 영수증 관리 (현금영수증 / 세금계산서)
// ============================================================================

const formatWon = (n) => `₩${Number(n || 0).toLocaleString('ko-KR')}`;

const formatDateTime = (iso) => {
  if (!iso) return '-';
  try {
    const d = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z');
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const STATUS_LABELS = {
  pending: { label: '대기', cls: 'badge-pending' },
  issued: { label: '발행완료', cls: 'badge-success' },
  failed: { label: '실패', cls: 'badge-cancelled' },
  cancelled: { label: '취소', cls: 'badge-cancelled' },
};

const TYPE_LABELS = {
  cash_receipt: '현금영수증',
  tax_invoice: '세금계산서',
  simple: '간이영수증',
};

let toastCounter = 0;

const ReceiptPage = () => {
  const { user } = useAuth();
  const storeId = user?.storeId || user?.store_id;

  const [tab, setTab] = useState('cash_receipt');
  const [receipts, setReceipts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [toasts, setToasts] = useState([]);
  const socketRef = useRef(null);

  // Issue modal
  const [issueModalOpen, setIssueModalOpen] = useState(false);
  const [recentPayments, setRecentPayments] = useState([]);
  const [issueForm, setIssueForm] = useState({
    type: 'cash_receipt',
    payment_id: '',
    identifier_type: 'phone',
    identifier: '',
    recipient_email: '',
    business_no: '',
    business_name: '',
    ceo_name: '',
    address: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const addToast = useCallback((msg, type = 'info') => {
    const id = ++toastCounter;
    setToasts((t) => [...t, { id, msg, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3200);
  }, []);

  const load = useCallback(async () => {
    if (!storeId) { setIsLoading(false); return; }
    setIsLoading(true);
    try {
      const res = await api.get('/admin/receipts', { params: { type: tab } });
      setReceipts(res.data?.receipts || []);
    } catch {
      addToast('영수증 목록을 불러오지 못했습니다', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [storeId, tab, addToast]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!storeId) return;
    const sock = connectAdminSocket(storeId);
    socketRef.current = sock;
    const onIssued = () => load();
    const onCancelled = () => load();
    sock.on('receipt_issued', onIssued);
    sock.on('receipt_cancelled', onCancelled);
    return () => {
      sock.off('receipt_issued', onIssued);
      sock.off('receipt_cancelled', onCancelled);
    };
  }, [storeId, load]);

  const loadRecentPayments = async () => {
    if (!storeId) return;
    try {
      // Best-effort: reports endpoint may return recent payments; fallback to empty list
      const res = await api.get('/reports/payments', { params: { store_id: storeId, limit: 30 } }).catch(() => ({ data: {} }));
      setRecentPayments(res.data?.payments || []);
    } catch {
      setRecentPayments([]);
    }
  };

  const openIssueModal = () => {
    setIssueForm({
      type: tab,
      payment_id: '',
      identifier_type: 'phone',
      identifier: '',
      recipient_email: '',
      business_no: '',
      business_name: '',
      ceo_name: '',
      address: '',
    });
    loadRecentPayments();
    setIssueModalOpen(true);
  };

  const closeIssueModal = () => setIssueModalOpen(false);

  const submitIssue = async () => {
    if (!issueForm.payment_id.trim()) {
      addToast('결제 ID를 입력해주세요', 'error');
      return;
    }
    setIsSubmitting(true);
    try {
      if (issueForm.type === 'cash_receipt') {
        if (!issueForm.identifier.trim()) {
          addToast('식별자를 입력해주세요', 'error');
          setIsSubmitting(false);
          return;
        }
        await api.post('/receipts/cash', {
          payment_id: issueForm.payment_id.trim(),
          identifier_type: issueForm.identifier_type,
          identifier: issueForm.identifier.trim(),
          recipient_email: issueForm.recipient_email.trim() || undefined,
        });
      } else {
        if (!issueForm.business_no.trim() || !issueForm.business_name.trim() || !issueForm.ceo_name.trim()) {
          addToast('사업자번호, 상호, 대표자명은 필수입니다', 'error');
          setIsSubmitting(false);
          return;
        }
        if (!issueForm.recipient_email.trim()) {
          addToast('이메일을 입력해주세요', 'error');
          setIsSubmitting(false);
          return;
        }
        await api.post('/receipts/tax-invoice', {
          payment_id: issueForm.payment_id.trim(),
          recipient_email: issueForm.recipient_email.trim(),
          business_no: issueForm.business_no.trim(),
          business_name: issueForm.business_name.trim(),
          ceo_name: issueForm.ceo_name.trim(),
          address: issueForm.address.trim() || undefined,
        });
      }
      addToast('영수증이 발행되었습니다', 'success');
      closeIssueModal();
      load();
    } catch (err) {
      addToast(err.response?.data?.error || '발행에 실패했습니다', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resendEmail = async (r) => {
    try {
      await api.post(`/receipts/${r.id}/email`);
      addToast('이메일이 발송되었습니다', 'success');
    } catch (err) {
      addToast(err.response?.data?.error || '이메일 발송 실패', 'error');
    }
  };

  const cancelReceipt = async (r) => {
    if (!window.confirm(`"${r.receipt_number}" 영수증을 취소하시겠습니까?`)) return;
    try {
      await api.post(`/receipts/${r.id}/cancel`, { reason: '매장 관리자 취소' });
      addToast('영수증이 취소되었습니다', 'success');
      load();
    } catch (err) {
      addToast(err.response?.data?.error || '취소에 실패했습니다', 'error');
    }
  };

  return (
    <div className="page-container">
      <div className="toast-container">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.type}`}>
            <div className="toast-content"><div className="toast-message">{t.msg}</div></div>
          </div>
        ))}
      </div>

      <div className="page-header">
        <div>
          <h1 className="page-title">영수증 관리</h1>
          <p className="page-subtitle">현금영수증 및 세금계산서 발행/조회</p>
        </div>
        <button className="btn btn-primary" onClick={openIssueModal}>
          + 발행하기
        </button>
      </div>

      <div className="tab-bar" style={{ marginBottom: 16 }}>
        <button className={`tab-btn ${tab === 'cash_receipt' ? 'active' : ''}`} onClick={() => setTab('cash_receipt')}>
          현금영수증
        </button>
        <button className={`tab-btn ${tab === 'tax_invoice' ? 'active' : ''}`} onClick={() => setTab('tax_invoice')}>
          세금계산서
        </button>
      </div>

      {isLoading ? (
        <div className="card"><div className="card-body">불러오는 중...</div></div>
      ) : receipts.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <h3>발행된 영수증이 없습니다</h3>
            <p>오른쪽 상단의 "발행하기" 버튼으로 새 영수증을 발행하세요.</p>
          </div>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div style={{ overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
                  <th style={thStyle}>발행일시</th>
                  <th style={thStyle}>종류</th>
                  <th style={thStyle}>금액</th>
                  <th style={thStyle}>VAT</th>
                  <th style={thStyle}>식별자</th>
                  <th style={thStyle}>영수증번호</th>
                  <th style={thStyle}>상태</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {receipts.map((r) => {
                  const status = STATUS_LABELS[r.status] || { label: r.status, cls: 'badge-cancelled' };
                  return (
                    <tr key={r.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={tdStyle}>{formatDateTime(r.issued_at || r.created_at)}</td>
                      <td style={tdStyle}>{TYPE_LABELS[r.type] || r.type}</td>
                      <td style={{ ...tdStyle, fontWeight: 700 }}>{formatWon(r.amount)}</td>
                      <td style={{ ...tdStyle, color: 'var(--text-secondary)' }}>{formatWon(r.vat)}</td>
                      <td style={tdStyle}>
                        <div style={{ fontSize: '0.75rem' }}>{r.identifier_type}</div>
                        <div style={{ fontSize: '0.85rem' }}>{r.identifier || '-'}</div>
                      </td>
                      <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.72rem' }}>
                        {r.receipt_number || '-'}
                      </td>
                      <td style={tdStyle}>
                        <span className={`badge ${status.cls}`}>{status.label}</span>
                      </td>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                        {r.status === 'issued' && (
                          <>
                            <button className="btn btn-ghost btn-sm" onClick={() => resendEmail(r)}>이메일</button>
                            <button className="btn btn-ghost btn-sm" onClick={() => cancelReceipt(r)} style={{ color: 'var(--danger)' }}>
                              취소
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Issue modal */}
      {issueModalOpen && (
        <>
          <div className="modal-overlay" onClick={closeIssueModal} />
          <div className="modal modal-lg" role="dialog" aria-label="영수증 발행">
            <div className="modal-header">
              <div className="modal-title">영수증 발행</div>
              <button className="modal-close" onClick={closeIssueModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">영수증 종류</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    className={`btn ${issueForm.type === 'cash_receipt' ? 'btn-primary' : 'btn-outline'}`}
                    style={{ flex: 1 }}
                    onClick={() => setIssueForm({ ...issueForm, type: 'cash_receipt' })}
                  >
                    현금영수증
                  </button>
                  <button
                    type="button"
                    className={`btn ${issueForm.type === 'tax_invoice' ? 'btn-primary' : 'btn-outline'}`}
                    style={{ flex: 1 }}
                    onClick={() => setIssueForm({ ...issueForm, type: 'tax_invoice' })}
                  >
                    세금계산서
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">결제 ID *</label>
                <input
                  className="form-input"
                  placeholder="결제 ID (payment_id)"
                  value={issueForm.payment_id}
                  onChange={(e) => setIssueForm({ ...issueForm, payment_id: e.target.value })}
                />
                {recentPayments.length > 0 && (
                  <select
                    className="form-select"
                    style={{ marginTop: 6 }}
                    value=""
                    onChange={(e) => e.target.value && setIssueForm({ ...issueForm, payment_id: e.target.value })}
                  >
                    <option value="">최근 결제에서 선택...</option>
                    {recentPayments.map((p) => (
                      <option key={p.id} value={p.id}>
                        {formatWon(p.amount)} · {formatDateTime(p.created_at)} · {p.id.slice(0, 8)}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {issueForm.type === 'cash_receipt' ? (
                <>
                  <div className="form-group">
                    <label className="form-label">식별자 유형</label>
                    <select
                      className="form-select"
                      value={issueForm.identifier_type}
                      onChange={(e) => setIssueForm({ ...issueForm, identifier_type: e.target.value })}
                    >
                      <option value="phone">휴대폰 (소득공제)</option>
                      <option value="business_no">사업자번호 (지출증빙)</option>
                      <option value="personal">주민번호 (소득공제)</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      식별자 값 *
                    </label>
                    <input
                      className="form-input"
                      placeholder={
                        issueForm.identifier_type === 'phone' ? '010-1234-5678' :
                        issueForm.identifier_type === 'business_no' ? '123-45-67890' :
                        '주민번호'
                      }
                      value={issueForm.identifier}
                      onChange={(e) => setIssueForm({ ...issueForm, identifier: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">이메일 (선택)</label>
                    <input
                      className="form-input"
                      type="email"
                      placeholder="receipt@example.com"
                      value={issueForm.recipient_email}
                      onChange={(e) => setIssueForm({ ...issueForm, recipient_email: e.target.value })}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="form-group">
                    <label className="form-label">사업자등록번호 *</label>
                    <input
                      className="form-input"
                      placeholder="123-45-67890"
                      value={issueForm.business_no}
                      onChange={(e) => setIssueForm({ ...issueForm, business_no: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">상호 *</label>
                    <input
                      className="form-input"
                      value={issueForm.business_name}
                      onChange={(e) => setIssueForm({ ...issueForm, business_name: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">대표자명 *</label>
                    <input
                      className="form-input"
                      value={issueForm.ceo_name}
                      onChange={(e) => setIssueForm({ ...issueForm, ceo_name: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">수신 이메일 *</label>
                    <input
                      className="form-input"
                      type="email"
                      value={issueForm.recipient_email}
                      onChange={(e) => setIssueForm({ ...issueForm, recipient_email: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">사업장 주소</label>
                    <input
                      className="form-input"
                      value={issueForm.address}
                      onChange={(e) => setIssueForm({ ...issueForm, address: e.target.value })}
                    />
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={closeIssueModal} disabled={isSubmitting}>취소</button>
              <button className="btn btn-primary" onClick={submitIssue} disabled={isSubmitting}>
                {isSubmitting ? '발행 중...' : '발행'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const thStyle = { textAlign: 'left', padding: '12px 16px', fontSize: '0.72rem', color: 'var(--text-tertiary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3 };
const tdStyle = { padding: '12px 16px', fontSize: '0.85rem', color: 'var(--text-primary)' };

export default ReceiptPage;
